import { useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore, type CSSProperties, type ReactNode } from 'react';
import { MoreHorizontal, Maximize2, Minimize2, Minus } from 'lucide-react';
import type { Controller } from '../host/controller';
import {resolveAgentContent} from '../content';
export const ADVISOR_ICON = 'https://www.thewhiskyexchange.com/media/rtwe/assets/application-v2/images/homepage/icon_privateclient.svg?v=25092025';

export function AdvisorDialog({ controller, children, hasSubmittedMessage }: { controller: Controller; children: ReactNode; hasSubmittedMessage: boolean }) {
  const chatContent = resolveAgentContent().chatWindow;
  const state = useSyncExternalStore(controller.subscribe, controller.getSnapshot);
  const dialog = useRef<HTMLDivElement>(null);
  const backdrop = useRef<HTMLDivElement>(null);
  const entryAnimations = useRef<Animation[]>([]);
  const menuButton = useRef<HTMLButtonElement>(null);
  const [menu, setMenu] = useState(false);
  useLayoutEffect(() => {
    if (!state.open) { setMenu(false); return; }
    const previousOverflow = document.body.style.overflow;
    const previousPadding = document.body.style.paddingRight;
    const scrollbar = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    if (scrollbar > 0) document.body.style.paddingRight = `${scrollbar}px`;
    const focusable = () => Array.from(dialog.current?.querySelectorAll<HTMLElement>('button:not(:disabled),a[href],input:not(:disabled),[tabindex="0"]') || []).filter(el => el.getClientRects().length > 0);
    const frame = requestAnimationFrame(() => dialog.current?.querySelector<HTMLInputElement>('input')?.focus({preventScroll: true}));
    const keydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); controller.hide('escape'); }
      if (event.key === 'Tab') {
        const items = focusable(); const first = items[0]; const last = items.at(-1);
        if (!items.length) { event.preventDefault(); dialog.current?.focus(); return; }
        if (event.shiftKey && (document.activeElement === first || !dialog.current?.contains(document.activeElement))) { event.preventDefault(); last?.focus(); }
        if (!event.shiftKey && (document.activeElement === last || !dialog.current?.contains(document.activeElement))) { event.preventDefault(); first?.focus(); }
      }
    };
    const focusin = (event: FocusEvent) => {
      if (!dialog.current?.contains(event.target as Node)) (focusable()[0] || dialog.current)?.focus();
    };
    document.addEventListener('keydown', keydown, true);
    document.addEventListener('focusin', focusin);
    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener('keydown', keydown, true);
      document.removeEventListener('focusin', focusin);
      document.body.style.overflow = previousOverflow;
      document.body.style.paddingRight = previousPadding;
      if (controller.opener?.isConnected) controller.opener.focus();
    };
  }, [state.open, controller]);
  useLayoutEffect(() => {
    const panel = dialog.current;
    if (!state.open || !panel) return;
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (motion.matches || !panel.animate) return;
    const origin = controller.openerBounds;
    const target = panel.getBoundingClientRect();
    const animations: Animation[] = [];
    if (origin && origin.width > 0 && origin.height > 0 && target.width > 0 && target.height > 0) {
      const x = origin.left + origin.width / 2 - target.left - target.width / 2;
      const y = origin.top + origin.height / 2 - target.top - target.height / 2;
      const sx = Math.max(.08, Math.min(1, origin.width / target.width));
      const sy = Math.max(.06, Math.min(1, origin.height / target.height));
      animations.push(panel.animate([
        {transform: `translate(${x}px, ${y}px) scale(${sx}, ${sy})`, opacity: .85},
        {transform: 'translate(0, 0) scale(1, 1)', opacity: 1},
      ], {duration: 320, easing: 'cubic-bezier(0.23, 1, 0.32, 1)'}));
    }
    if (backdrop.current) animations.push(backdrop.current.animate([{opacity: 0}, {opacity: 1}], {duration: 280, easing: 'cubic-bezier(0.23, 1, 0.32, 1)'}));
    entryAnimations.current = animations;
    const cancel = () => animations.forEach(animation => animation.cancel());
    const preferenceChange = () => {if (motion.matches) cancel();};
    motion.addEventListener('change', preferenceChange);
    window.addEventListener('resize', cancel);
    return () => {cancel(); motion.removeEventListener('change', preferenceChange); window.removeEventListener('resize', cancel);};
  }, [state.open, controller]);
  // Expand/Restore stay immediate; they never inherit an in-flight entry transform.
  useLayoutEffect(() => {entryAnimations.current.forEach(animation => animation.cancel());}, [state.expanded]);
  useEffect(() => {
    if (menu) dialog.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
  }, [menu]);
  return <div className="twe-drinks-advisor" hidden={!state.open}>
    <div className="twe-drinks-advisor__overlay">
      <div ref={backdrop} className="twe-drinks-advisor__backdrop" aria-hidden="true"/>
      <div ref={dialog} role="dialog" aria-modal="true" aria-labelledby="twe-advisor-title" tabIndex={-1} className={`twe-drinks-advisor__dialog${state.expanded ? ' twe-drinks-advisor__dialog--expanded' : ''}`} style={{'--twe-drinks-advisor-dialog-width': state.dialogWidth} as CSSProperties}>
        <header className="twe-drinks-advisor__header">
          <div className="twe-drinks-advisor__brand"><img src={ADVISOR_ICON} alt="" width="29" height="32"/><div className="twe-drinks-advisor__brand-copy"><h2 id="twe-advisor-title">{chatContent.windowTitle}</h2><span className="twe-drinks-advisor__online-status"><i aria-hidden="true"/><span>{chatContent.onlineStatusLabel}</span></span></div></div>
          <div className="twe-drinks-advisor__controls">
            <div className="twe-drinks-advisor__menu-wrap" onBlur={e => {if (!e.currentTarget.contains(e.relatedTarget)) setMenu(false);}}>
              <button ref={menuButton} type="button" aria-label={chatContent.conversationMenuLabel} title={chatContent.conversationMenuLabel} aria-haspopup="menu" aria-expanded={menu} onClick={() => setMenu(!menu)}><MoreHorizontal size={21}/></button>
              {menu && <div role="menu" className="twe-drinks-advisor__menu"><button type="button" role="menuitem" className="twe-drinks-advisor__menu-item" onClick={() => {controller.resetConversation(); setMenu(false); menuButton.current?.focus();}}>{chatContent.newConversationMenuItem}</button>{hasSubmittedMessage && <button type="button" role="menuitem" className="twe-drinks-advisor__menu-item twe-drinks-advisor__menu-item--disabled" disabled>{chatContent.emailHistoryMenuItem}</button>}</div>}
            </div>
            <button type="button" className="twe-drinks-advisor__expand-control" aria-label={state.expanded ? chatContent.restoreControlLabel : chatContent.expandControlLabel} title={state.expanded ? chatContent.restoreControlLabel : chatContent.expandControlLabel} onClick={state.expanded ? controller.restore : controller.expand}>{state.expanded ? <Minimize2 size={18}/> : <Maximize2 size={18}/>}</button>
            <button type="button" aria-label={chatContent.minimiseControlLabel} title={chatContent.minimiseControlLabel} onClick={() => controller.hide('minimise')}><Minus size={22}/></button>
          </div>
        </header>
        {children}
      </div>
    </div>
  </div>;
}
